import { reloadPage } from 'js-utils/http';
import { deepKeysToCamel } from 'js-utils/transformation';

import * as analytics from '@eb/site-analytics';

import {
    editCollection as editCollectionApi,
    getShareUrl as getShareUrlApi,
    followCollection as followCollectionApi,
    unfollowCollection as unfollowCollectionApi,
} from '../api/collection';
import { removeEventsFromCollection as removeEventsFromCollectionApi } from '../api/events';
import { toggleEditMode } from './ui';
import { setLoading, setError } from './index';
import {
    COLLECTION_PAGE_GA_CATEGORY,
    REMOVE_COLLECTION_GA_ACTION,
    SAVE_COLLECTION_GA_ACTION,
    FOLLOW_GA_ACTION,
    UNFOLLOW_GA_ACTION,
    EDITORIAL_COLLECTION_PAGE_GA_CATEGORY,
} from '../constants';

const { trackEventFromState } = analytics;

export const UPDATE_COLLECTION = 'userCollection/collection/update_collection';

export const updateCollection = (data) => ({
    type: UPDATE_COLLECTION,
    payload: data,
});

export const editCollection = (collectionProperties) => (
    dispatch,
    getState,
) => {
    const {
        collection: { id },
    } = getState();

    return editCollectionApi(id, collectionProperties).then(
        (response) => dispatch(updateCollection(deepKeysToCamel(response))),
        () => dispatch(setError()),
    );
};

export const getShareUrl = () => (dispatch, getState) => {
    const {
        collection: { id },
    } = getState();

    return getShareUrlApi(id).then(
        (response) => dispatch(updateCollection(deepKeysToCamel(response))),
        () => dispatch(setError()),
    );
};

const removeEventsFromCollection = () => (dispatch, getState) => {
    const {
        collection: { id },
        form,
        gaSettings,
    } = getState();

    const formValues = form.collection.values;
    const { eventsToRemove } = formValues;

    if (eventsToRemove && eventsToRemove.length) {
        trackEventFromState(
            { gaSettings },
            {
                action: REMOVE_COLLECTION_GA_ACTION,
                label: id,
                category: COLLECTION_PAGE_GA_CATEGORY,
            },
        );
        return removeEventsFromCollectionApi(id, eventsToRemove).then(
            () => reloadPage(),
            () => dispatch(setError()),
        );
    }

    return false;
};

export const saveCollectionEdits = () => (dispatch, getState) => {
    const {
        collection: { id },
        form,
        gaSettings,
    } = getState();

    const formValues = form.collection.values;

    const { summary, name, imageLarge } = formValues;

    dispatch(setLoading(true));

    trackEventFromState(
        { gaSettings },
        {
            action: SAVE_COLLECTION_GA_ACTION,
            label: id,
            category: COLLECTION_PAGE_GA_CATEGORY,
        },
    );

    return dispatch(
        editCollection({
            summary,
            name,
            // must set image_id to null in order to actually delete it
            image_id: imageLarge || null,
        }),
    )
        .then(() => dispatch(removeEventsFromCollection()))
        .then(() => dispatch(setLoading(false)))
        .then(() => dispatch(toggleEditMode()));
};

export const followCollection = (setLocalLoadingState) => (
    dispatch,
    getState,
) => {
    const {
        collection: { id, isQuality },
        gaSettings,
    } = getState();

    setLocalLoadingState(true);

    followCollectionApi(id).then(
        (response) => {
            dispatch(
                updateCollection({ isFollowing: response.followed_by_you }),
            );
            setLocalLoadingState(false);

            trackEventFromState(
                { gaSettings },
                {
                    action: FOLLOW_GA_ACTION,
                    label: id,
                    category: isQuality
                        ? EDITORIAL_COLLECTION_PAGE_GA_CATEGORY
                        : COLLECTION_PAGE_GA_CATEGORY,
                },
            );
        },
        () => {
            dispatch(updateCollection({ isFollowing: true }));
            setLocalLoadingState(false);
            dispatch(setError());
        },
    );
};

export const unfollowCollection = (setLocalLoadingState) => (
    dispatch,
    getState,
) => {
    const {
        collection: { id, isQuality },
        gaSettings,
    } = getState();

    setLocalLoadingState(true);

    unfollowCollectionApi(id).then(
        (response) => {
            dispatch(
                updateCollection({ isFollowing: response.followed_by_you }),
            );
            setLocalLoadingState(false);

            trackEventFromState(
                { gaSettings },
                {
                    action: UNFOLLOW_GA_ACTION,
                    label: id,
                    category: isQuality
                        ? EDITORIAL_COLLECTION_PAGE_GA_CATEGORY
                        : COLLECTION_PAGE_GA_CATEGORY,
                },
            );
        },
        () => {
            setLocalLoadingState(false);
            dispatch(setError());
        },
    );
};

export const getFollowStatus = () => (dispatch, getState) => {
    const {
        collection: { id },
    } = getState();

    followCollectionApi(id).then(
        (response) => {
            if (response.action_performed) {
                // If it was successful, then the user isn't following the collection
                // unfollow, set the UI follow state to false, and then call the callback
                unfollowCollectionApi(id);
                dispatch(updateCollection({ isFollowing: false }));
            } else {
                dispatch(updateCollection({ isFollowing: true }));
            }
        },
        (error) => {
            if (
                error &&
                error.parsedError &&
                error.parsedError.argumentErrors
            ) {
                if (
                    error.parsedError.argumentErrors.collection_id ===
                    'PROHIBITED'
                ) {
                    // likely already following collection
                    dispatch(updateCollection({ isFollowing: true }));
                }
            } else {
                dispatch(updateCollection({ isFollowing: false }));
            }
        },
    );
};
